import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BackIcon } from '../../assets/images/icons/back.svg';
import { gqlTypes } from '../../types';
import * as types from '../../types';
import EditCreditCardBillingInfo from '../../views/Checkout/pages/Billing/EditCreditCardBillingInfo';
import styles from './EditBilling.module.scss';
import LocaleContext from '../../contexts/LocaleContext';

export interface BillingFields {
  firstName: string;
  lastName: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  paymentInfo: {
    token?: string;
    lastFour?: number;
    type?: string;
    month?: number;
    year?: number;
  };
}

interface EditBillingProps {
  onUpdateBilling: (billingInfo: BillingFields, recaptchaToken: string) => void;
  onCancel: () => void;
  initialBilling?: types.BillingInfo | null;
  shippingAddress?: types.Address;
}

export default function EditBilling({ onUpdateBilling, onCancel, initialBilling, shippingAddress }: EditBillingProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const { country } = useContext(LocaleContext);

  const clearError = () => setError('');

  const handleUpdateBillingInfo = useCallback(
    (args: {
      input: gqlTypes.BillingAccountInput;
      firstName: string;
      lastName: string;
      recaptchaToken: string;
    }): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        setSubmitting(true);
        try {
          const billingFields: BillingFields = {
            firstName: args.firstName,
            lastName: args.lastName,
            address: {
              line1: args.input.address?.line1 || '',
              line2: args.input.address?.line2 || '',
              city: args.input.address?.city || '',
              state: args.input.address?.state || '',
              zip: args.input.address?.zip || '',
              country: args.input.address?.country || country,
            },
            paymentInfo: {
              token: args.input.billingInfo?.token,
            },
          };

          onUpdateBilling(billingFields, args.recaptchaToken);
          resolve();
        } catch (err) {
          setError(t('there_was_an_error_updating_your_billing_information_please_try_again'));
          reject(err);
        }
      });
    },
    [onUpdateBilling, country, t],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BackIcon onClick={onCancel} className={styles.backButton} />
        <h3>{t('edit_billing')}</h3>
        <div className={styles.headerPlaceholder} />
      </div>

      <div className={styles.body}>
        <EditCreditCardBillingInfo
          updateBillingInfo={handleUpdateBillingInfo}
          billingInfo={initialBilling}
          shippingAddress={shippingAddress}
          submitting={submitting}
          setError={setError}
          clearError={clearError}
          actionText={t('update')}
          compactForm={true}
          outlineErrorFields={true}
        />
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  );
}
